import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import FieldRenderer from "./FieldRenderer";

export default function ListField({ field, value, onChange }) {
  const { label, item, primitive } = field;
  const isPrimitive = !!primitive;

  const blank = () => {
    if (isPrimitive) return "";
    const o = {};
    for (const f of item) o[f.name] = "";
    return o;
  };

  const update = (idx, partial) => {
    const next = [...value];
    if (isPrimitive) next[idx] = partial;
    else next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const add = () => onChange([...(value || []), blank()]);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="border border-white/10 rounded-xl p-4 bg-[#0A0B10]/40">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
          {label} <span className="text-gray-600 normal-case">({value.length})</span>
        </span>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-[#7B2CBF]/15 text-[#7B2CBF] border border-[#7B2CBF]/30 hover:bg-[#7B2CBF]/25"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>

      {value.length === 0 && (
        <p className="text-xs text-gray-600 italic">No items. Click "Add" to create one.</p>
      )}

      <div className="space-y-3">
        {value.map((row, idx) => (
          <div key={idx} className="border border-white/5 rounded-lg p-3 bg-[#0D0E13]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-mono text-gray-500">#{idx + 1}</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="text-gray-500 hover:text-white disabled:opacity-30 p-1"
                  title="Move up"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === value.length - 1}
                  className="text-gray-500 hover:text-white disabled:opacity-30 p-1"
                  title="Move down"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-gray-500 hover:text-red-400 p-1"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {isPrimitive ? (
              <FieldRenderer field={item[0]} value={row} onChange={(v) => update(idx, v)} />
            ) : (
              <div className="space-y-3">
                {item.map((sub) => (
                  <FieldRenderer
                    key={sub.name}
                    field={sub}
                    value={row?.[sub.name]}
                    onChange={(v) => update(idx, { [sub.name]: v })}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
